import Image from 'next/image';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';

import pinGray from './assets/pin-gray.svg';
import pinWhite from './assets/pin-white.svg';

import ArtistList from '@/components/Artist/ArtistList';
import { ArtistListItemProps } from '@/components/Artist/ArtistListItem';
import EventAbout from '@/components/Event/EventAbout';
import EventBuyTicketsContainer from '@/components/Event/EventBuyTicketsContainer';
import EventTicketsStatus from '@/components/Event/EventBuyTicketsContainer/EventTicketsStatus';
import EventItineraryBox from '@/components/Event/EventItineraryBox';
import EventVenueItem from '@/components/Event/EventVenueItem';
import ImageWithFallback from '@/components/ImageWithFallback';
import BlurredBackground from '@/components/Module/BlurredBackground';
import Divider from '@/components/Module/Divider';
import IconButton from '@/components/Module/IconButton';
import exclamation from '@/components/Pages/PurchaseDetail/assets/exclamation.svg';
import TicketType from '@/components/Pages/TicketSelection/TicketType';
import useViewport from '@/hooks/useViewport';
import { Price } from '@/types/price';
import { extractCityAndState } from '@/utils';

export type EventDetailProps = {
  id: string;
  imageUrl: string;
  venueImageUrl: string;
  audienceMinimumAge: number;
  status: EventTicketsStatus;
  venueId: string;
  mapUrl: string;
  title: string;
  venueName: string;
  venueAddress: string;
  venueWebsiteUrl: string | null;
  itinerary: string;
  description: string;
  artists: ArtistListItemProps[];
  ticketTypes: TicketType[];
  children?: ReactNode;
};

const priceRange = (
  ticketTypes: TicketType[],
): [] | [Price] | [Price, Price] => {
  if (ticketTypes.length === 0) {
    return [];
  }

  if (ticketTypes.length === 1) {
    return [
      { amount: ticketTypes[0].basePrice, currency: ticketTypes[0].currency },
    ];
  }

  const minPriceTicket = ticketTypes.reduce((acc, cur) => {
    return acc.basePrice.greaterThan(cur.basePrice) ? cur : acc;
  });
  const maxPriceTicket = ticketTypes.reduce((acc, cur) => {
    return acc.basePrice.lessThan(cur.basePrice) ? cur : acc;
  });

  if (minPriceTicket.basePrice.equals(maxPriceTicket.basePrice)) {
    return [
      { amount: minPriceTicket.basePrice, currency: minPriceTicket.currency },
    ];
  }

  return [
    { amount: minPriceTicket.basePrice, currency: minPriceTicket.currency },
    { amount: maxPriceTicket.basePrice, currency: maxPriceTicket.currency },
  ];
};

const MobileEventDetail = (props: EventDetailProps) => {
  const {
    id,
    imageUrl,
    venueImageUrl,
    audienceMinimumAge,
    title,
    venueName,
    venueAddress,
    itinerary,
    description,
    artists,
    mapUrl,
    ticketTypes,
    venueId,
    children,
    status,
    venueWebsiteUrl,
  } = props;

  const price = priceRange(ticketTypes);

  const share = () => {
    global?.window?.navigator?.share(
      {
        url: global?.window?.location?.href,
        title: title,
        text: description,
      },
      {
        facebook: false,
      },
    );
  };

  const cityAndState = extractCityAndState(venueAddress);

  return (
    <div className={`relative`}>
      <BlurredBackground imageUrl={imageUrl} height={375}>
        <div className={`absolute bottom-[69px] right-[24px]`}>
          <IconButton type={`upload`} floating={true} onClick={share} />
        </div>
      </BlurredBackground>
      <div className={`relative mt-[-45px] px-[24px]`}>
        <p className={`break-words text-h1 font-bold text-gray-600`}>{title}</p>
        <p className={`break-words pt-[8px] text-b2 font-normal text-gray-300`}>
          {venueName}
        </p>
        <p
          className={`flex items-center gap-[8px] pt-[10px] text-b3 font-normal text-gray-50`}
        >
          <Image width={18} height={18} src={pinGray} alt="pin" />
          {cityAndState ? cityAndState.join(`, `) : venueAddress}
        </p>
        <EventItineraryBox itinerary={itinerary} />

        <Divider my={32} />
        <div className="flex flex-col gap-[24px]">
          <p className="text-h4 font-bold text-gray-300">About</p>
          <EventAbout description={description} maxLengthInShort={165} />
          {audienceMinimumAge > 0 ? (
            <p className={`flex gap-[4.5px] text-b2 font-normal text-gray-400`}>
              <Image
                width={16}
                height={16}
                src={exclamation}
                alt="exclamation"
              />
              This is a {audienceMinimumAge}+ event
            </p>
          ) : null}
        </div>
        <Divider my={32} />
        <ArtistList artists={artists}>
          <Divider my={32} />
        </ArtistList>

        <div className="flex flex-col gap-[24px]">
          <p className="text-h4 font-bold text-gray-300">Venue</p>
          <EventVenueItem
            venueAddress={venueAddress}
            mapUrl={mapUrl}
            venueImageUrl={venueImageUrl}
            venueName={venueName}
            venueId={venueId}
          />
        </div>
      </div>
      <div className="fixed bottom-0 z-[1] w-full">
        <EventBuyTicketsContainer
          id={id}
          priceRange={price}
          status={status}
          venueWebsiteUrl={venueWebsiteUrl}
        />
      </div>

      {children && (
        <div className={`absolute top-0 bottom-0 z-[10] w-screen`}>
          <div className="h-full w-full bg-gray-0 opacity-90"></div>
          <div className={`fixed bottom-0 h-screen w-screen`}>{children}</div>
        </div>
      )}
    </div>
  );
};

const TabletEventDetail = (props: EventDetailProps) => {
  const {
    id,
    imageUrl,
    venueImageUrl,
    title,
    venueName,
    venueAddress,
    audienceMinimumAge,
    itinerary,
    description,
    artists,
    mapUrl,
    ticketTypes,
    venueId,
    children,
    status,
    venueWebsiteUrl,
  } = props;

  const price = priceRange(ticketTypes);

  const share = () => {
    global?.window?.navigator?.share(
      {
        url: global?.window?.location?.href,
        title: title,
        text: description,
      },
      {
        facebook: false,
      },
    );
  };
  const cityAndState = extractCityAndState(venueAddress);

  return (
    <div className={`relative`}>
      <BlurredBackground imageUrl={imageUrl} height={468}>
        <div className={`absolute bottom-[69px] right-[24px]`}>
          <IconButton type={`upload`} floating={true} onClick={share} />
        </div>
      </BlurredBackground>
      <div className={`relative mt-[-45px] px-[24px]`}>
        <p className={`break-words text-h1 font-bold text-gray-600`}>{title}</p>
        <p className={`break-words pt-[8px] text-b2 font-normal text-gray-300`}>
          {venueName}
        </p>
        <p
          className={`flex items-center gap-[8px] pt-[10px] text-b3 font-normal text-gray-50`}
        >
          <Image width={18} height={18} src={pinGray} alt="pin" />
          {cityAndState ? cityAndState.join(`, `) : venueAddress}
        </p>
        <EventItineraryBox itinerary={itinerary} />

        <Divider my={80} />
        <div className="flex flex-col gap-[40px]">
          <p className="text-h1 font-bold text-gray-500">About</p>
          <EventAbout description={description} maxLengthInShort={420} />
          {audienceMinimumAge > 0 ? (
            <p className={`flex gap-[4.5px] text-b2 font-normal text-gray-400`}>
              <Image
                width={16}
                height={16}
                src={exclamation}
                alt="exclamation"
              />
              This is a {audienceMinimumAge}+ event
            </p>
          ) : null}
        </div>

        <Divider my={80} />

        <ArtistList artists={artists}>
          <Divider my={80} />
        </ArtistList>

        <div className="flex flex-col gap-[40px]">
          <p className="text-h1 font-bold text-gray-500">Venue</p>
          <EventVenueItem
            venueAddress={venueAddress}
            mapUrl={mapUrl}
            venueImageUrl={venueImageUrl}
            venueName={venueName}
            venueId={venueId}
          />
        </div>
      </div>
      <div className="fixed bottom-0 z-[1] w-full">
        <EventBuyTicketsContainer
          id={id}
          priceRange={price}
          status={status}
          venueWebsiteUrl={venueWebsiteUrl}
        />
      </div>

      {children && (
        <div className={`fixed bottom-0 z-[10] h-screen w-screen`}>
          <div className="z-[11] h-full w-full bg-gray-0 opacity-90"></div>
          <div className={`absolute bottom-0 h-screen w-screen`}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const DesktopEventDetail = (props: EventDetailProps) => {
  const {
    id,
    imageUrl,
    venueImageUrl,
    title,
    venueName,
    venueAddress,
    audienceMinimumAge,
    itinerary,
    description,
    artists,
    mapUrl,
    ticketTypes,
    venueId,
    children,
    status,
    venueWebsiteUrl,
  } = props;

  const price = priceRange(ticketTypes);

  const share = () => {
    global?.window?.navigator?.share(
      {
        url: global?.window?.location?.href,
        title: title,
        text: description,
      },
      {
        facebook: false,
      },
    );
  };

  const cityAndState = extractCityAndState(venueAddress);
  const router = useRouter();
  const closeChildren = () => {
    router.replace(`/events/${id}`);
  };

  return (
    <div className={`relative`}>
      <BlurredBackground imageUrl={imageUrl} height={504} />
      <div className={`relative mx-auto mt-[-400px] w-[1248px] px-[24px]`}>
        <div className={`flex w-full items-center justify-between gap-[49px]`}>
          <div className={`relative h-[344px] shrink-0 grow`}>
            <ImageWithFallback
              key={imageUrl}
              placeholder="blur"
              blurDataURL={imageUrl}
              width={344}
              height={344}
              src={imageUrl}
              alt="event-image"
              className={`rounded-[29px] object-cover`}
            />
            <div className={`absolute bottom-[24px] right-[24px] z-10`}>
              <IconButton type={`upload`} floating={true} onClick={share} />
            </div>
          </div>
          <div className={`flex w-full flex-col gap-[40px]`}>
            <div className={`flex flex-col gap-[24px]`}>
              <p
                className={`mt-[18px] break-words text-h0 font-bold text-gray-600`}
              >
                {title}
              </p>
              <div className="flex flex-col gap-[4px]">
                <div className={`flex w-full items-center gap-[15px]`}>
                  <span className="break-words text-b2 font-normal text-gray-300">
                    {venueName}
                  </span>
                  <p
                    className={`flex items-center gap-[4px] text-b3 font-normal text-gray-300`}
                  >
                    <Image width={18} height={18} src={pinWhite} alt="pin" />
                    {cityAndState ? cityAndState.join(`, `) : venueAddress}
                  </p>
                </div>
                <p className={`text-h4 font-medium text-green-100`}>
                  {itinerary}
                </p>
              </div>
            </div>
            <div className={`flex max-w-full`}>
              <EventBuyTicketsContainer
                id={id}
                priceRange={price}
                status={status}
                venueWebsiteUrl={venueWebsiteUrl}
              />
            </div>
          </div>
        </div>

        <Divider my={79} />
        <div className="flex flex-col gap-[40px]">
          <p className="text-h1 font-bold text-gray-500">About</p>
          <EventAbout description={description} maxLengthInShort={420} />
          {audienceMinimumAge > 0 ? (
            <p className={`flex gap-[4.5px] text-b2 font-normal text-gray-400`}>
              <Image
                width={16}
                height={16}
                src={exclamation}
                alt="exclamation"
              />
              This is a {audienceMinimumAge}+ event
            </p>
          ) : null}
        </div>

        <Divider my={80} />
        <ArtistList artists={artists}>
          <Divider my={80} />
        </ArtistList>

        <div className="flex flex-col gap-[40px]">
          <p className="text-h1 font-bold text-gray-500">Venue</p>
          <EventVenueItem
            venueAddress={venueAddress}
            mapUrl={mapUrl}
            venueImageUrl={venueImageUrl}
            venueName={venueName}
            venueId={venueId}
          />
        </div>
      </div>

      {children && (
        <div className={`fixed bottom-0 z-[11] h-screen w-screen`}>
          <div className="z-[11] h-full w-full bg-gray-0 opacity-90"></div>
          <div
            className={`absolute bottom-0 flex h-full w-full items-center justify-center`}
            onClick={closeChildren}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const EventDetail = (props: EventDetailProps) => {
  const {} = props;
  const viewport = useViewport();

  if (viewport == `mobile`) {
    return <MobileEventDetail {...props} />;
  }
  if (viewport == `tablet`) {
    return <TabletEventDetail {...props} />;
  }
  if (viewport == `desktop`) {
    return <DesktopEventDetail {...props} />;
  }

  return null;
};

export default EventDetail;
